import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Verify() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    axios.get(`${API}/verify/${code}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({
        loading: false, data: null,
        error: e.response?.status === 404 ? "not_found" : "server",
      }));
  }, [code]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#050505] text-[#FAFAFA]"
         data-testid="verify-page">
      <div className="w-full max-w-xl border border-[#1f1f1f] bg-[#0a0a0a] p-10">
        <div className="crest justify-center mb-3">
          <span className="text-[#D4AF37] text-2xl leading-none">◇</span>
          <span>The Boardroom</span>
        </div>
        <p className="overline text-center">Certificate Verification</p>
        <div className="gold-divider w-24 mx-auto my-6" />

        {state.loading && (
          <div className="flex flex-col items-center py-8" data-testid="verify-loading">
            <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
            <p className="text-neutral-500 text-sm mt-4 font-body">Verifying…</p>
          </div>
        )}

        {state.error === "not_found" && (
          <div className="text-center py-6" data-testid="verify-not-found">
            <XCircle className="mx-auto text-red-400 mb-4" size={56} />
            <p className="font-display text-3xl text-red-300">Not a valid Boardroom certificate</p>
            <p className="text-sm text-neutral-500 mt-3 font-body">
              Code <span className="font-mono text-[#AA8A25]">{code}</span> was not found in our records.
            </p>
          </div>
        )}

        {state.error === "server" && (
          <p className="text-center text-neutral-400 py-6" data-testid="verify-error">
            Unable to reach the verification service. Please try again shortly.
          </p>
        )}

        {state.data && state.data.valid && (
          <div className="text-center py-4" data-testid="verify-valid">
            <CheckCircle2 className="mx-auto text-[#D4AF37] mb-4" size={64} />
            <p className="overline text-[#AA8A25]">Verified</p>
            <p className="font-display text-4xl md:text-5xl mt-2">This certificate is authentic.</p>
            <div className="gold-divider w-16 mx-auto my-6" />
            <p className="text-xs uppercase tracking-widest text-[#AA8A25]">Issued To</p>
            <p className="font-display text-3xl text-white mt-2">{state.data.name}</p>
            <p className="text-xs uppercase tracking-widest text-[#AA8A25] mt-6">Issued On</p>
            <p className="text-neutral-200 font-body mt-1">
              {new Date(state.data.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-neutral-600 mt-8">
              Code · <span className="font-mono text-[#D4AF37]">{state.data.code}</span>
            </p>
          </div>
        )}

        {state.data && !state.data.valid && (
          <div className="text-center py-4" data-testid="verify-revoked">
            <XCircle className="mx-auto text-red-400 mb-4" size={64} />
            <p className="overline text-red-400">Revoked</p>
            <p className="font-display text-4xl mt-2">This certificate has been revoked.</p>
            <p className="text-sm text-neutral-500 mt-4 font-body">
              The Boardroom no longer recognises certificate <span className="font-mono text-neutral-300">{state.data.code}</span> as valid.
              Contact the Boardroom for details.
            </p>
          </div>
        )}

        <div className="text-center mt-10">
          <Link to="/login" className="text-[10px] uppercase tracking-widest text-neutral-600 hover:text-[#D4AF37]">
            ◇ Enter The Boardroom
          </Link>
        </div>
      </div>
    </div>
  );
}
